import React, { useState } from 'react';
import { ExternalLink, Users, Calendar, MapPin, Briefcase, GraduationCap, Heart, BookOpen } from 'lucide-react';

const AlumniPage = () => {
  const [activeTab, setActiveTab] = useState('network');

  const alumni = [
    {
      name: "Dr. Adebola Johnson",
      graduationYear: 2010,
      currentRole: "Senior Software Engineer at TechCorp",
      location: "Lagos, Nigeria",
      image: " "
    },
    {
      name: "Eng. Chinedu Okoro",
      graduationYear: 2015,
      currentRole: "Civil Engineer at BuildRight Ltd",
      location: "Abuja, Nigeria",
      image: " "
    },
    {
      name: "Ms. Funmi Adeyemi",
      graduationYear: 2018,
      currentRole: "Marketing Director at BrandWorks",
      location: "London, UK",
      image: " "
    },
    {
      name: "Dr. Tunde Williams",
      graduationYear: 2005,
      currentRole: "University Lecturer",
      location: "Boston, USA",
      image: " "
    }
  ];

  const events = [
    {
      title: "Annual Alumni Homecoming",
      date: "December 15, 2023",
      location: "Main Campus Auditorium",
      description: "Reconnect with old friends and faculty at our yearly gathering"
    },
    {
      title: "Career Networking Workshop",
      date: "October 28, 2023",
      location: "Virtual Event",
      description: "Learn how to leverage your alumni network for career growth"
    },
    {
      title: "Mentorship Program Launch",
      date: "November 10, 2023",
      location: "Online",
      description: "Join our new mentorship program connecting students with alumni"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <ExternalLink className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Alumni Network</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay connected with your alma mater and fellow graduates from around the world
          </p>
        </div>

        {/* Alumni Portal Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white mb-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Alumni Portal Access</h2>
          <p className="mb-6 max-w-2xl mx-auto">
            Connect with fellow alumni, update your information, and access exclusive resources through our dedicated alumni portal.
          </p>
          <a 
            href="https://fedpolybidaalumni.com.ng/index.html" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center bg-white text-blue-700 font-bold py-3 px-6 rounded-lg hover:bg-blue-50 transition"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            Visit Alumni Portal
          </a>
        </div>

        {/* Tabs */}
       <div className="flex justify-center border-b border-gray-200 mb-8">
        {['network', 'events', 'benefits', 'give-back'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-4 px-6 font-medium ${activeTab === tab ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab === 'network' && 'Alumni Network'}
            {tab === 'events' && 'Events'}
            {tab === 'benefits' && 'Benefits'}
            {tab === 'give-back' && 'Give Back'}
          </button>
        ))}
      </div>

        {/* Tab Content */}
        {activeTab === 'network' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Featured Alumni</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {alumni.map((alum, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                  <div className="flex items-center mb-4">
                    <img src={alum.image} alt={alum.name} className="w-16 h-16 rounded-full object-cover" />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">{alum.name}</h3>
                      <p className="text-gray-600">Class of {alum.graduationYear}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-600">
                      <Briefcase className="w-4 h-4 mr-2" />
                      <span>{alum.currentRole}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{alum.location}</span>
                    </div>
                  </div>
                  <button className="mt-4 w-full bg-blue-100 text-blue-600 py-2 rounded-lg font-medium hover:bg-blue-200 transition">
                    Connect
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Upcoming Events</h2>
            <div className="space-y-6">
              {events.map((event, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
                  <div className="flex flex-wrap gap-4 mb-3">
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{event.description}</p>
                  <button className="bg-blue-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-blue-700 transition">
                    Register
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'benefits' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Alumni Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: "Career Services",
                  description: "Access to job postings, career counseling, and networking events",
                  icon: <Briefcase className="w-6 h-6 text-blue-600" />
                },
                {
                  title: "Library Access",
                  description: "Continued access to our digital library resources",
                  icon: <BookOpen className="w-6 h-6 text-blue-600" />
                },
                {
                  title: "Campus Facilities",
                  description: "Discounts on sports facilities and event spaces",
                  icon: <GraduationCap className="w-6 h-6 text-blue-600" />
                },
                {
                  title: "Continuing Education",
                  description: "Special rates on professional development courses",
                  icon: <Heart className="w-6 h-6 text-blue-600" />
                }
              ].map((benefit, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'give-back' && (
          <div>
            <h2 className="flex justify-center text-2xl font-bold text-gray-900 mb-6">Ways to Give Back</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: "Mentorship Program",
                  description: "Guide current students in their academic and career journeys",
                  icon: <Users className="w-6 h-6 text-blue-600" />
                },
                {
                  title: "Guest Speaking",
                  description: "Share your expertise by speaking at campus events",
                  icon: <Heart className="w-6 h-6 text-blue-600" />
                },
                {
                  title: "Scholarship Fund",
                  description: "Support deserving students through our scholarship program",
                  icon: <GraduationCap className="w-6 h-6 text-blue-600" />
                },
                {
                  title: "Internship Opportunities",
                  description: "Provide internship opportunities at your organization",
                  icon: <Briefcase className="w-6 h-6 text-blue-600" />
                }
              ].map((way, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                    {way.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{way.title}</h3>
                  <p className="text-gray-600">{way.description}</p>
                  <button className="mt-4 bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition">
                    Get Involved
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alumni Stats */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">10,000+</div>
            <div className="text-gray-600">Alumni Worldwide</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">45+</div>
            <div className="text-gray-600">Countries Represented</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">15+</div>
            <div className="text-gray-600">Alumni Chapters</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-700 mb-2">95%</div>
            <div className="text-gray-600">Employment Rate</div>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 bg-blue-600 rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Stay Connected</h2>
          <p className="mb-6 max-w-2xl mx-auto">
            Join our alumni newsletter to stay updated on events, news, and opportunities
          </p>
          <div className="max-w-md mx-auto flex">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-l-lg text-gray-900 focus:outline-none"
            />
            <button className="bg-yellow-500 text-blue-900 font-bold py-3 px-6 rounded-r-lg hover:bg-yellow-400 transition">
              Subscribe
            </button>
          </div>
        </div>

        {/* Additional Portal Link */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">Looking for the alumni portal?</p>
          <a 
            href="https://fedpolybidaalumni.com.ng/index.html" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            Access Alumni Portal at fedpolybidaalumni.com.ng
          </a>
        </div>
      </div>
    </div>
  );
};

export default AlumniPage;